import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, Search, X, Save, ListTodo, ChevronLeft, ChevronRight, AlertTriangle, Trash2 } from 'lucide-react';
import { AgendaProgressBarVertical } from '../components/AgendaProgressBar';
import { Sidebar } from '../components/Sidebar';

const AZUL = '#1E3A8A';
const FUNDO = '#EEF2FF';
const BORDA = '#DBEAFE';

const STATUS_LABEL = {
  pendente:    { label: 'Pendente',    bg: '#EEF2FF', color: AZUL },
  em_andamento:{ label: 'Em andamento',bg: '#dbeafe', color: '#1d4ed8' },
  concluida:   { label: 'Concluída',   bg: '#dcfce7', color: '#15803d' },
  atrasada:    { label: 'Atrasada',    bg: '#fee2e2', color: '#dc2626' },
  cancelada:   { label: 'Cancelada',   bg: '#f3f4f6', color: '#6b7280' },
};
const PRIORIDADE_LABEL = { baixa: 'Baixa', media: 'Média', alta: 'Alta', urgente: 'Urgente' };
const PRIORIDADE_COLOR = { baixa: '#22c55e', media: '#eab308', alta: '#f97316', urgente: '#ef4444' };
const ABAS = [
  { key: 'pendentes', label: 'Pendentes' },
  { key: 'concluidas', label: 'Concluídas' },
  { key: 'todas', label: 'Todas' },
  { key: 'minhas', label: 'Minhas' },
];

const inp = `w-full rounded-lg px-3 py-2 text-sm text-black focus:outline-none`;
const inpStyle = { backgroundColor: FUNDO, border: `1px solid ${BORDA}` };

const fmtDate = (s) => { if (!s) return '—'; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
const todayStr = () => new Date().toISOString().slice(0, 10);
const emptyForm = () => ({
  titulo: '', descricao: '', data_inicio: todayStr(), data_conclusao_prevista: todayStr(),
  prioridade: 'media', visibilidade: 'equipe', cor_etiqueta: '',
});

export default function Agenda({ user, onLogout }) {
  const navigate = useNavigate();
  const company = JSON.parse(localStorage.getItem('company') || '{}');
  const isFuncionario = user?.role === 'FUNCIONARIO' || user?.role === 'funcionario';

  const [aba, setAba] = useState('pendentes');
  const [activities, setActivities] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ pendentes: 0, em_andamento: 0, atrasadas: 0, concluidas: 0 });
  const [busca, setBusca] = useState('');
  const [prioridadeFiltro, setPrioridadeFiltro] = useState('');
  const [responsavelFiltro, setResponsavelFiltro] = useState('');
  const [funcionarios, setFuncionarios] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [tipoAgendamento, setTipoAgendamento] = useState('periodo');
  const [selectedResp, setSelectedResp] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifCiente, setNotifCiente] = useState(false);

  const loadSummary = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/agenda/activities/summary');
      setSummary(res.data);
    } catch { /* não bloqueia */ }
  }, []);

  const loadFuncionarios = useCallback(async () => {
    if (!company.id) return;
    try {
      const res = await axiosInstance.get(`/funcionarios/users/${company.id}`);
      setFuncionarios(res.data);
    } catch { /* sem funcionários cadastrados */ }
  }, [company.id]);

  const loadActivities = useCallback(async () => {
    setLoading(true);
    try {
      const params = { aba, page, page_size: pageSize };
      if (busca) params.busca = busca;
      if (prioridadeFiltro) params.prioridade = prioridadeFiltro;
      if (responsavelFiltro) params.responsavel_id = responsavelFiltro;
      const res = await axiosInstance.get('/agenda/activities', { params });
      setActivities(res.data.items);
      setTotal(res.data.total);
    } catch {
      toast.error('Erro ao carregar atividades');
    } finally {
      setLoading(false);
    }
  }, [aba, page, busca, prioridadeFiltro, responsavelFiltro]);

  useEffect(() => {
    loadSummary();
    loadFuncionarios();
    if (isFuncionario) {
      axiosInstance.get('/agenda/notifications?only_unread=true')
        .then(r => setNotifications(r.data || []))
        .catch(() => {});
    }
  }, [loadSummary, loadFuncionarios]); // eslint-disable-line
  useEffect(() => { loadActivities(); }, [loadActivities]);
  useEffect(() => { setPage(1); }, [aba, busca, prioridadeFiltro, responsavelFiltro]);

  const toggleResp = (key) => {
    setSelectedResp(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const openNew = () => {
    setForm(emptyForm());
    setTipoAgendamento('periodo');
    setSelectedResp(new Set([`gestor:${user?.id}`]));
    setShowForm(true);
  };

  const handleCreate = async () => {
    if (form.titulo.trim().length < 5) { toast.error('Título deve ter ao menos 5 caracteres'); return; }
    if (form.descricao.trim().length < 10) { toast.error('Descrição deve ter ao menos 10 caracteres'); return; }
    if (form.data_conclusao_prevista < form.data_inicio) { toast.error('Data de conclusão deve ser igual ou posterior à data de início'); return; }
    if (selectedResp.size === 0) { toast.error('Selecione ao menos um responsável'); return; }

    const responsaveis = Array.from(selectedResp).map(key => {
      const [tipo, id] = key.split(':');
      if (tipo === 'gestor') return { tipo, id, nome: user?.name || 'Gestor' };
      const f = funcionarios.find(e => e.id === id);
      return { tipo, id, nome: f ? f.name : '' };
    });

    setSaving(true);
    try {
      await axiosInstance.post('/agenda/activities', { ...form, responsaveis });
      toast.success('Atividade criada!');
      setShowForm(false);
      loadActivities();
      loadSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar atividade');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (activity) => {
    if (!window.confirm('Deseja realmente excluir essa atividade?')) return;
    try {
      await axiosInstance.delete(`/agenda/activities/${activity.id}`);
      toast.success('Atividade excluída!');
      loadActivities();
      loadSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir atividade');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const dismissNotifications = async () => {
    for (const n of notifications) {
      axiosInstance.put(`/agenda/notifications/${n.id}/read`).catch(() => {});
    }
    setNotifications([]);
    setNotifCiente(false);
  };

  return (
    <div className="flex min-h-screen bg-zinc-950">
      <Sidebar user={user} onLogout={onLogout} />
      <div className="flex-1 p-3 pt-14 md:p-8 overflow-x-hidden" style={{ backgroundColor: FUNDO }}>
    <div className="animate-fade-in space-y-4 md:space-y-6 pb-10">

      {notifications.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="rounded-2xl p-6 w-full max-w-md shadow-2xl bg-white" style={{ border: `1px solid ${BORDA}` }}>
            <h2 className="text-base font-bold mb-1" style={{ color: AZUL }}>Notificação da Agenda</h2>
            <p className="text-xs mb-4" style={{ color: AZUL, opacity: 0.6 }}>Você tem {notifications.length} notificação(ões) não lida(s):</p>
            <div className="space-y-2 mb-5">
              {notifications.map(n => (
                <div key={n.id} className="rounded-lg px-3 py-2 text-sm" style={{ backgroundColor: FUNDO, border: `1px solid ${BORDA}`, color: '#374151' }}>
                  {n.mensagem}
                </div>
              ))}
            </div>
            <label className="flex items-center gap-3 cursor-pointer mb-5 select-none">
              <input
                type="checkbox"
                checked={notifCiente}
                onChange={e => setNotifCiente(e.target.checked)}
                className="w-4 h-4 rounded cursor-pointer"
                style={{ accentColor: AZUL }}
              />
              <span className="text-sm font-medium" style={{ color: AZUL }}>Estou ciente</span>
            </label>
            <button
              onClick={dismissNotifications}
              disabled={!notifCiente}
              className="w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-opacity"
              style={{ backgroundColor: AZUL, opacity: notifCiente ? 1 : 0.35, cursor: notifCiente ? 'pointer' : 'not-allowed' }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: AZUL }}>Agenda</h1>
          <p className="text-sm mt-0.5" style={{ color: AZUL, opacity: 0.6 }}>Atividades, compromissos e diário de acompanhamento</p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium text-sm transition-opacity hover:opacity-90"
          style={{ backgroundColor: AZUL }}
        >
          <Plus size={16} /> Nova Atividade
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Pendentes',    value: summary.pendentes,    color: AZUL },
          { label: 'Em Andamento', value: summary.em_andamento, color: '#1d4ed8' },
          { label: 'Atrasadas',    value: summary.atrasadas,    color: '#dc2626' },
          { label: 'Concluídas',   value: summary.concluidas,   color: '#15803d' },
        ].map(c => (
          <div key={c.label} className="rounded-xl p-4 bg-white" style={{ border: `1px solid ${BORDA}` }}>
            <p className="text-xs text-black">{c.label}</p>
            <p className="text-2xl font-bold mt-1" style={{ color: c.color }}>{c.value}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-2 pb-3 overflow-x-auto" style={{ borderBottom: `1px solid ${BORDA}` }}>
        {ABAS.map(a => (
          <button
            key={a.key}
            onClick={() => setAba(a.key)}
            className="px-4 py-2 text-sm font-semibold rounded-xl transition-colors whitespace-nowrap"
            style={aba === a.key
              ? { backgroundColor: AZUL, color: '#fff' }
              : { backgroundColor: BORDA, color: AZUL }
            }
          >
            {a.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: AZUL, opacity: 0.5 }} />
          <input
            className="w-full rounded-lg pl-9 pr-3 py-2 text-sm text-black focus:outline-none"
            style={inpStyle}
            placeholder="Buscar por título ou descrição..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
          />
        </div>
        <select
          className="rounded-lg px-3 py-2 text-sm text-black focus:outline-none"
          style={inpStyle}
          value={prioridadeFiltro}
          onChange={e => setPrioridadeFiltro(e.target.value)}
        >
          <option value="">Todas prioridades</option>
          {Object.entries(PRIORIDADE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {!isFuncionario && (
          <select
            className="rounded-lg px-3 py-2 text-sm text-black focus:outline-none"
            style={inpStyle}
            value={responsavelFiltro}
            onChange={e => setResponsavelFiltro(e.target.value)}
          >
            <option value="">Todos responsáveis</option>
            {user && <option value={user.id}>{user.name} (eu)</option>}
            {funcionarios.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: AZUL, borderTopColor: 'transparent' }} />
        </div>
      ) : activities.length === 0 ? (
        <div className="text-center py-16" style={{ color: AZUL, opacity: 0.4 }}>
          <ListTodo size={40} className="mx-auto mb-3 opacity-30" />
          <p>Nenhuma atividade encontrada</p>
        </div>
      ) : (
        <>
        {/* Cards — mobile */}
        <div className="md:hidden space-y-3">
          {activities.map(a => {
            const sc = STATUS_LABEL[a.status] || STATUS_LABEL.pendente;
            return (
              <div
                key={a.id}
                onClick={() => navigate(`/agenda/atividade/${a.id}`)}
                className="rounded-xl p-4 cursor-pointer active:opacity-70 bg-white"
                style={{ border: `1px solid ${BORDA}`, backgroundColor: a.status === 'atrasada' ? '#fff1f2' : '#fff' }}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <span className="font-semibold text-sm text-black leading-tight">{a.titulo}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0" style={{ backgroundColor: sc.bg, color: sc.color }}>{sc.label}</span>
                </div>
                {a.descricao && <p className="text-xs mb-2 truncate" style={{ color: AZUL, opacity: 0.5 }}>{a.descricao}</p>}
                <div className="flex items-center gap-2 text-xs mb-2" style={{ color: AZUL, opacity: 0.7 }}>
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: PRIORIDADE_COLOR[a.prioridade] || '#9ca3af' }} />
                  <span>{PRIORIDADE_LABEL[a.prioridade]}</span>
                  <span>·</span>
                  <span>{fmtDate(a.data_inicio)}{a.data_inicio !== a.data_conclusao_prevista ? ` → ${fmtDate(a.data_conclusao_prevista)}` : ''}</span>
                </div>
                {a.responsaveis?.length > 0 && (
                  <p className="text-xs mb-2" style={{ color: AZUL, opacity: 0.6 }}>👤 {a.responsaveis.map(r => r.nome).join(', ')}</p>
                )}
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: BORDA }}>
                    <div className="h-full rounded-full" style={{ backgroundColor: AZUL, width: `${a.progresso || 0}%` }} />
                  </div>
                  <span className="text-xs font-medium" style={{ color: AZUL }}>{a.progresso || 0}%</span>
                </div>
                {a.precisa_preencher_hoje && (
                  <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                    <AlertTriangle size={12} /> Diário pendente hoje
                  </p>
                )}
              </div>
            );
          })}
          {totalPages > 1 && (
            <div className="flex items-center justify-between py-2">
              <span className="text-xs" style={{ color: AZUL, opacity: 0.6 }}>Pág. {page}/{totalPages}</span>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="p-1.5 disabled:opacity-30 rounded-lg" style={{ color: AZUL, border: `1px solid ${BORDA}` }}><ChevronLeft size={14} /></button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="p-1.5 disabled:opacity-30 rounded-lg" style={{ color: AZUL, border: `1px solid ${BORDA}` }}><ChevronRight size={14} /></button>
              </div>
            </div>
          )}
        </div>

        {/* Tabela — desktop */}
        <div className="hidden md:block rounded-xl overflow-hidden bg-white" style={{ border: `1px solid ${BORDA}` }}>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase" style={{ backgroundColor: BORDA, color: AZUL }}>
              <tr>
                <th className="text-left px-4 py-3">Atividade</th>
                <th className="text-left px-4 py-3">Prioridade</th>
                <th className="text-left px-4 py-3">Responsável</th>
                <th className="text-left px-4 py-3">Início</th>
                <th className="text-left px-4 py-3">Conclusão</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Progresso</th>
                <th className="text-left px-4 py-3">Diário</th>
                <th className="text-left px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {activities.map(a => {
                const sc = STATUS_LABEL[a.status] || STATUS_LABEL.pendente;
                return (
                  <tr
                    key={a.id}
                    onClick={() => navigate(`/agenda/atividade/${a.id}`)}
                    className="cursor-pointer transition-colors"
                    style={{
                      borderTop: `1px solid ${BORDA}`,
                      backgroundColor: a.status === 'atrasada' ? '#fff1f2' : 'transparent',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = FUNDO}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = a.status === 'atrasada' ? '#fff1f2' : 'transparent'}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-black">{a.titulo}</div>
                      <div className="text-xs truncate max-w-xs" style={{ color: AZUL, opacity: 0.5 }}>{a.descricao}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: PRIORIDADE_COLOR[a.prioridade] || '#9ca3af' }} />
                        <span className="text-xs text-black">{PRIORIDADE_LABEL[a.prioridade] || '—'}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 text-black text-xs">{a.responsaveis.map(r => r.nome).join(', ') || '—'}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: AZUL, opacity: 0.7 }}>{fmtDate(a.data_inicio)}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: AZUL, opacity: 0.7 }}>{fmtDate(a.data_conclusao_prevista)}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ backgroundColor: sc.bg, color: sc.color }}>
                        {sc.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <AgendaProgressBarVertical value={a.progresso || 0} />
                    </td>
                    <td className="px-4 py-3">
                      {a.precisa_preencher_hoje ? (
                        <span className="flex items-center gap-1 text-xs text-orange-600">
                          <AlertTriangle size={14} />
                          Pendente hoje
                        </span>
                      ) : (
                        <span style={{ color: AZUL, opacity: 0.3 }}>—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={e => { e.stopPropagation(); handleDelete(a); }}
                        title="Excluir"
                        className="p-1.5 rounded-lg transition-colors hover:bg-red-100"
                        style={{ color: AZUL, opacity: 0.4 }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = '#dc2626'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = '0.4'; e.currentTarget.style.color = AZUL; }}
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: `1px solid ${BORDA}` }}>
              <span className="text-xs" style={{ color: AZUL, opacity: 0.6 }}>Página {page} de {totalPages} · {total} atividade(s)</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-1.5 disabled:opacity-30 rounded-lg"
                  style={{ color: AZUL, border: `1px solid ${BORDA}` }}
                >
                  <ChevronLeft size={14} />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="p-1.5 disabled:opacity-30 rounded-lg"
                  style={{ color: AZUL, border: `1px solid ${BORDA}` }}
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
        </>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="rounded-2xl p-6 w-full max-w-lg shadow-2xl my-8 bg-white" style={{ border: `1px solid ${BORDA}` }}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold" style={{ color: AZUL }}>Nova Atividade</h2>
              <button onClick={() => setShowForm(false)} style={{ color: AZUL, opacity: 0.5 }}>
                <X size={20} />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs mb-1 block text-black">Título da Atividade *</label>
                <input
                  className={inp} style={inpStyle}
                  value={form.titulo}
                  onChange={e => setForm({ ...form, titulo: e.target.value })}
                  placeholder="Ex: Levantamento de Projeto"
                />
              </div>

              <div>
                <label className="text-xs mb-1 block text-black">Descrição *</label>
                <textarea
                  className={`${inp} min-h-[80px] resize-y`} style={inpStyle}
                  value={form.descricao}
                  onChange={e => setForm({ ...form, descricao: e.target.value })}
                  placeholder="O que precisa ser feito..."
                />
              </div>

              {!isFuncionario && (
                <div>
                  <label className="text-xs mb-1 block text-black">Responsável(is) *</label>
                  <div className="rounded-lg p-2 max-h-36 overflow-y-auto space-y-1" style={{ backgroundColor: FUNDO, border: `1px solid ${BORDA}` }}>
                    <label className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm text-black hover:bg-white">
                      <input type="checkbox" checked={selectedResp.has(`gestor:${user?.id}`)} onChange={() => toggleResp(`gestor:${user?.id}`)} style={{ accentColor: AZUL }} />
                      {user?.name} (eu — gestor)
                    </label>
                    {funcionarios.map(f => (
                      <label key={f.id} className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm text-black hover:bg-white">
                        <input type="checkbox" checked={selectedResp.has(`funcionario:${f.id}`)} onChange={() => toggleResp(`funcionario:${f.id}`)} style={{ accentColor: AZUL }} />
                        {f.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs mb-2 block text-black">Tipo de agendamento</label>
                <div className="flex gap-4">
                  {[{ value: 'unico', label: 'Único dia' }, { value: 'periodo', label: 'Período' }].map(op => (
                    <label key={op.value} className="flex items-center gap-2 cursor-pointer text-sm text-black">
                      <input
                        type="radio"
                        name="tipoAgendamento"
                        value={op.value}
                        checked={tipoAgendamento === op.value}
                        onChange={() => {
                          setTipoAgendamento(op.value);
                          if (op.value === 'unico') {
                            setForm(f => ({ ...f, data_conclusao_prevista: f.data_inicio }));
                          }
                        }}
                        style={{ accentColor: AZUL }}
                      />
                      {op.label}
                    </label>
                  ))}
                </div>
              </div>

              {tipoAgendamento === 'unico' ? (
                <div>
                  <label className="text-xs mb-1 block text-black">Data *</label>
                  <input
                    type="date" className={inp} style={inpStyle}
                    value={form.data_inicio}
                    onChange={e => setForm({ ...form, data_inicio: e.target.value, data_conclusao_prevista: e.target.value })}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs mb-1 block text-black">Data de Início *</label>
                    <input
                      type="date" className={inp} style={inpStyle}
                      value={form.data_inicio}
                      onChange={e => setForm({ ...form, data_inicio: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs mb-1 block text-black">Data de Conclusão *</label>
                    <input
                      type="date" className={inp} style={inpStyle}
                      value={form.data_conclusao_prevista}
                      onChange={e => setForm({ ...form, data_conclusao_prevista: e.target.value })}
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs mb-1 block text-black">Prioridade</label>
                  <select className={inp} style={inpStyle} value={form.prioridade} onChange={e => setForm({ ...form, prioridade: e.target.value })}>
                    {Object.entries(PRIORIDADE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs mb-1 block text-black">Visibilidade</label>
                  <select className={inp} style={inpStyle} value={form.visibilidade} onChange={e => setForm({ ...form, visibilidade: e.target.value })}>
                    <option value="privada">Privada</option>
                    <option value="equipe">Equipe</option>
                    <option value="publica">Pública</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs mb-1 block text-black">Cor/Etiqueta</label>
                <input
                  type="color"
                  className="w-16 h-9 rounded-lg cursor-pointer"
                  style={{ border: `1px solid ${BORDA}` }}
                  value={form.cor_etiqueta || AZUL}
                  onChange={e => setForm({ ...form, cor_etiqueta: e.target.value })}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-sm rounded-lg transition-colors text-black"
                style={{ border: `1px solid ${BORDA}` }}
              >
                Cancelar
              </button>
              <button
                onClick={handleCreate}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2 text-white rounded-lg font-medium text-sm transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: AZUL }}
              >
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={15} />}
                Criar Atividade
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
      </div>
    </div>
  );
}
